import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useDynamicLogo } from "@/lib/useLogo";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import CurrencySwitcher from "@/components/site/CurrencySwitcher";

export default function Navbar({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const logoUrl = useDynamicLogo();

  const links = [
    { to: "/", label: t("nav.stay") },
    { to: "/rooms", label: t("nav.rooms") },
    { to: "/reviews", label: t("nav.reviews") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
    { to: "/policies/cancellation", label: t("nav.policies") },
  ];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const useLight = transparent && !scrolled;
  const bgClass = useLight
    ? "bg-gradient-to-b from-black/30 to-transparent border-transparent text-white"
    : "berdhill-glass border-b border-brand-border text-brand-text";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass}`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={logoUrl} alt="Berdhill Rooms" className="h-12 md:h-14 lg:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm tracking-wide">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `transition-colors duration-300 hover:opacity-70 ${isActive ? "font-medium" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <CurrencySwitcher light={useLight} />
          <LanguageSwitcher light={useLight} />
          {user ? (
            <>
              <Link
                to={user.role === "admin" || user.role === "staff" ? "/staff" : "/my-bookings"}
                data-testid="nav-account-link"
                className="text-sm px-3 hover:opacity-70"
              >
                {user.role === "admin" || user.role === "staff" ? t("nav.staff") : t("nav.mybookings")}
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                className="text-sm px-2 hover:opacity-70"
              >
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="text-sm px-3 hover:opacity-70">
              {t("nav.signin")}
            </Link>
          )}
          <Link
            to="/rooms"
            data-testid="nav-book-now-btn"
            className="bg-brand-primary text-white hover:bg-brand-primaryHover transition-colors duration-300 px-5 py-2.5 rounded-sm text-sm font-medium"
          >
            {t("nav.booknow")}
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <CurrencySwitcher light={useLight} />
          <LanguageSwitcher light={useLight} />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden bg-brand-bg border-t border-brand-border text-brand-text">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base"
              >
                {l.label}
              </Link>
            ))}
            <hr className="border-brand-border" />
            {user ? (
              <>
                <Link to={user.role === "admin" || user.role === "staff" ? "/staff" : "/my-bookings"} className="text-base">
                  {user.role === "admin" || user.role === "staff" ? t("nav.staff") : t("nav.mybookings")}
                </Link>
                <button onClick={logout} className="text-base text-left">{t("nav.logout")}</button>
              </>
            ) : (
              <Link to="/login" className="text-base">{t("nav.signin")}</Link>
            )}
            <Link to="/rooms" className="bg-brand-primary text-white text-center px-5 py-3 rounded-sm text-sm">
              {t("nav.booknow")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
